import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

interface DropdownItem {
  name: string;
  path: string;
  icon: string;
  description?: string;
}

interface NavItem {
  name: string;
  path: string;
  hasDropdown?: boolean;
  dropdown?: DropdownItem[];
}

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { activeTheme } = useTheme();

  // Cores do tema ativo
  const primary = activeTheme.colors.primary;
  const secondary = activeTheme.colors.secondary;
  const accent = activeTheme.colors.accent;

  // ✅ CORREÇÃO: nome correto é 'Corporativo', não 'Tema Corporativo IBX'
  const isCorporativo = activeTheme.id === 'corporativo';
  const ctaColor = isCorporativo ? accent : accent;

  const primaryLight = `${primary}15`;
  const primaryLighter = `${primary}22`;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileOpenDropdown(null);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleMouseEnter = (name: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const navItems: NavItem[] = [
    { name: 'Home', path: '/' },
    { name: 'Quem Somos', path: '/quem-somos' },
    {
      name: 'Seguros',
      path: '/servicos',
      hasDropdown: true,
      dropdown: [
        { name: 'Incêndio Empresarial', path: '/seguros/seguro-incendio', icon: 'ri-fire-line', description: 'Cobertura especializada contra incêndios' },
        { name: 'Seguro Auto', path: '/seguros/seguro-auto', icon: 'ri-car-line', description: 'Proteção completa para seu veículo' },
        { name: 'Seguro de Vida', path: '/seguros/seguro-vida', icon: 'ri-heart-pulse-line', description: 'Segurança para você e sua família' },
        { name: 'Seguro Empresarial', path: '/seguros/seguro-empresarial', icon: 'ri-building-line', description: 'Proteja seu negócio contra riscos' },
        { name: 'Seguro Residencial', path: '/seguros/seguro-residencial', icon: 'ri-home-smile-line', description: 'Seu lar protegido 24 horas' },
        { name: 'Seguro Patrimonial', path: '/seguros/seguro-patrimonial', icon: 'ri-safe-line', description: 'Proteção abrangente do patrimônio' },
      ],
    },
    {
      name: 'Estratégia',
      path: '/servicos',
      hasDropdown: true,
      dropdown: [
        { name: 'Gestão de Contratos', path: '/estrategia/gestao-de-contratos', icon: 'ri-file-list-3-line', description: 'Controle total da sua carteira' },
        { name: 'Consultoria Técnica', path: '/estrategia/consultoria-tecnica', icon: 'ri-search-eye-line', description: 'Análise especializada de riscos' },
        { name: 'Suporte e Acompanhamento', path: '/estrategia/suporte-acompanhamento', icon: 'ri-customer-service-2-line', description: 'Atendimento dedicado' },
      ],
    },
    {
      name: 'Conteúdo',
      path: '/blog',
      hasDropdown: true,
      dropdown: [
        { name: 'Blog', path: '/blog', icon: 'ri-article-line', description: 'Artigos e dicas sobre seguros' },
        { name: 'Casos de Sucesso', path: '/cases', icon: 'ri-trophy-line', description: 'Histórias reais de economia e proteção' },
        { name: 'Comunidade', path: '/comunidade', icon: 'ri-community-line', description: 'Conecte-se com outros profissionais' },
        { name: 'Taxa de Corretagem 10%', path: '/taxa-corretagem', icon: 'ri-percent-line', description: 'Entenda como funciona nossa taxa' },
        { name: 'Perguntas Frequentes', path: '/faq', icon: 'ri-question-answer-line', description: 'Tire suas dúvidas sobre seguros' },
      ],
    },
    {
      name: 'Resultados',
      path: '/cases',
      hasDropdown: true,
      dropdown: [
        { name: 'Cases de Sucesso', path: '/cases', icon: 'ri-bar-chart-line', description: 'Resultados reais dos nossos clientes' },
        { name: 'Solicitar Análise', path: '/cotacao', icon: 'ri-search-eye-line', description: 'Descubra quanto você pode economizar' },
      ],
    },
    { name: 'Contato', path: '/contato' },
  ];

  const isActive = (item: NavItem) => {
    if (item.path === '/') return location.pathname === '/';
    if (item.hasDropdown && item.dropdown) {
      return item.dropdown.some((d) => location.pathname === d.path) || location.pathname.startsWith(item.path);
    }
    return location.pathname === item.path;
  };

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
      }`}
    >
      {/* Barra superior com gradiente corporativo */}
      <div
        className="h-1 w-full"
        style={{ 
          background: isCorporativo
            ? `linear-gradient(to right, ${primary}, ${secondary}, ${ctaColor})`
            : `linear-gradient(to right, ${primary}, ${ctaColor}, ${primary})` 
        }}
      />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[68px]">

          {/* Logo com cores corporativas */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shadow-md transition-all duration-300 group-hover:scale-105"
              style={{ 
                background: isCorporativo
                  ? `linear-gradient(135deg, ${primary}, ${secondary})`
                  : `linear-gradient(135deg, ${ctaColor}, ${primary})` 
              }}
            >
              <i className="ri-shield-check-fill text-white text-lg"></i>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold text-gray-900 tracking-tight">IX Brasil</span>
              <span 
                className="text-[10px] font-semibold tracking-widest uppercase" 
                style={{ color: ctaColor }}
              >
                Gestão de Riscos
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.hasDropdown ? handleMouseEnter(item.name) : undefined}
                onMouseLeave={() => item.hasDropdown ? handleMouseLeave() : undefined}
              >
                {item.hasDropdown ? (
                  <>
                    <button
                      className="flex items-center gap-1 px-3.5 py-2 rounded-md text-[13.5px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                      style={
                        isActive(item) || activeDropdown === item.name
                          ? { 
                              color: isCorporativo ? primary : ctaColor, 
                              backgroundColor: primaryLight 
                            }
                          : {}
                      }
                      onMouseEnter={e => {
                        if (!(isActive(item) || activeDropdown === item.name)) {
                          (e.currentTarget as HTMLButtonElement).style.color = isCorporativo ? primary : ctaColor;
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = primaryLight;
                        }
                      }}
                      onMouseLeave={e => {
                        if (!(isActive(item) || activeDropdown === item.name)) {
                          (e.currentTarget as HTMLButtonElement).style.color = '';
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
                        }
                      }}
                    >
                      <span className={isActive(item) || activeDropdown === item.name ? '' : 'text-gray-700'}>
                        {item.name}
                      </span>
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}></i>
                    </button>

                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                          style={{ minWidth: '260px' }}
                          onMouseEnter={() => handleMouseEnter(item.name)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="py-2">
                            {item.dropdown?.map((sub) => (
                              <Link
                                key={sub.path + sub.name}
                                to={sub.path}
                                className="flex items-center gap-3 px-4 py-2.5 transition-colors duration-150 group cursor-pointer hover:bg-gray-50"
                              >
                                <div
                                  className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-150 flex-shrink-0"
                                  style={{ backgroundColor: primaryLight }}
                                >
                                  <i 
                                    className={`${sub.icon} text-base`} 
                                    style={{ color: isCorporativo ? primary : ctaColor }}
                                  ></i>
                                </div>
                                <div>
                                  <div className="text-[13px] font-semibold text-gray-800 leading-tight group-hover:text-gray-900 transition-colors duration-150">
                                    {sub.name}
                                  </div>
                                  {sub.description && (
                                    <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{sub.description}</div>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className="flex items-center px-3.5 py-2 rounded-md text-[13.5px] font-medium transition-all duration-200 whitespace-nowrap"
                    style={
                      isActive(item) 
                        ? { 
                            color: isCorporativo ? primary : ctaColor, 
                            backgroundColor: primaryLight 
                          } 
                        : { color: '#374151' }
                    }
                    onMouseEnter={e => {
                      if (!isActive(item)) {
                        (e.currentTarget as HTMLAnchorElement).style.color = isCorporativo ? primary : ctaColor;
                        (e.currentTarget as HTMLAnchorElement).style.backgroundColor = primaryLight;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive(item)) {
                        (e.currentTarget as HTMLAnchorElement).style.color = '#374151';
                        (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '';
                      }
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Desktop CTAs - Botão laranja no tema corporativo */}
          <div className="hidden lg:flex items-center gap-2.5">
            <a
              href="https://wa.me/5511999999999?text=Olá%2C%20gostaria%20de%20falar%20com%20a%20IX%20Brasil."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white text-[13px] font-semibold rounded-lg transition-all duration-200 hover:bg-[#1ebe5d] hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base"></i>
              WhatsApp
            </a>
            <Link
              to="/cotacao"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-white text-[13px] font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg whitespace-nowrap cursor-pointer"
              style={{
                backgroundColor: ctaColor,
                boxShadow: `0 4px 14px ${ctaColor}44`,
              }}
            >
              <i className="ri-search-eye-line text-base"></i>
              Solicitar Análise
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-gray-700 transition-colors cursor-pointer"
            style={{ }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = primaryLight)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
            aria-label="Menu"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22 }}
              className="lg:hidden overflow-hidden border-t border-gray-100"
            >
              <div className="py-3 space-y-0.5">
                {navItems.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <>
                        <button
                          onClick={() => setMobileOpenDropdown(mobileOpenDropdown === item.name ? null : item.name)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-[13.5px] font-medium transition-colors duration-200 cursor-pointer"
                          style={
                            isActive(item)
                              ? { 
                                  backgroundColor: primaryLight, 
                                  color: isCorporativo ? primary : ctaColor 
                                }
                              : { color: '#374151' }
                          }
                        >
                          <span>{item.name}</span>
                          <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileOpenDropdown === item.name ? 'rotate-180' : ''}`}></i>
                        </button>
                        <AnimatePresence>
                          {mobileOpenDropdown === item.name && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.18 }}
                              className="overflow-hidden ml-3 mt-0.5 space-y-0.5 pl-3"
                              style={{ borderLeft: `2px solid ${primaryLighter}` }}
                            >
                              {item.dropdown?.map((sub) => (
                                <Link
                                  key={sub.path + sub.name}
                                  to={sub.path}
                                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                                >
                                  <div
                                    className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0"
                                    style={{ backgroundColor: primaryLight }}
                                  >
                                    <i 
                                      className={`${sub.icon} text-sm`} 
                                      style={{ color: isCorporativo ? primary : ctaColor }}
                                    ></i>
                                  </div>
                                  <span className="text-[13px] font-medium text-gray-700">{sub.name}</span>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className="block px-4 py-3 rounded-lg text-[13.5px] font-medium transition-colors duration-200"
                        style={
                          isActive(item)
                            ? { 
                                backgroundColor: primaryLight, 
                                color: isCorporativo ? primary : ctaColor 
                              }
                            : { color: '#374151' }
                        }
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2.5 py-4 border-t border-gray-100">
                <a
                  href="https://wa.me/5511999999999?text=Olá%2C%20gostaria%20de%20falar%20com%20a%20IX%20Brasil."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white text-sm font-bold rounded-lg transition-all duration-200 hover:bg-[#1ebe5d] whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Falar no WhatsApp
                </a>
                <Link
                  to="/cotacao"
                  className="flex items-center justify-center gap-2 px-6 py-3 text-white text-sm font-bold rounded-lg transition-all duration-200 hover:opacity-90 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: ctaColor }}
                >
                  <i className="ri-search-eye-line text-lg"></i>
                  Solicitar Análise Gratuita
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;